import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import FaQTableData from "Views/UsefulCorners/FAQ/data/FaQTableData";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { Link } from "react-router-dom";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { toast } from "react-toastify";
import { getAllFaQs, deleteFaQ } from "services/faqService";
import Dropdown from "components/CustomDropdown/Dropdown";

function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");

  // Fetch all FAQs
  const fetchFaQs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) {
        params.FaQType = filterType;
      }
      const response = await getAllFaQs(params);
      if (response.success) {
        setFaqs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error(error.message || "Failed to fetch FAQs");
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete FAQ
  const handleDelete = async () => {
    if (!selectedFaq?._id) return;

    setLoading(true);
    try {
      const response = await deleteFaQ(selectedFaq._id);
      if (response.success) {
        toast.success(response.message || "FAQ deleted successfully!");
        setOpen(false);
        setSelectedFaq(null);
        fetchFaQs();
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error(error.message || "Failed to delete FAQ");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (faq) => {
    setSelectedFaq(faq);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFaq(null);
  };

  // Search and filter
  const filteredFaqs = faqs.filter((faq) => {
    const search = searchTerm.toLowerCase();
    return (
      faq.question?.toLowerCase().includes(search) ||
      faq.answer?.toLowerCase().includes(search) ||
      faq.FaQType?.toLowerCase().includes(search)
    );
  });

  const { columns, rows } = FaQTableData(filteredFaqs, handleDeleteClick);

  // Fetch FAQs on component mount
  useEffect(() => {
    fetchFaQs();
  }, [filterType]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={3}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All FAQs ({filteredFaqs.length})
                </ArgonTypography>

                <Link to="/faq/add">
                  <ArgonButton variant="gradient" color="info" size="small">
                    + Add FAQ
                  </ArgonButton>
                </Link>
              </ArgonBox>

              {/* Search and Filter */}
              <ArgonBox display="flex" gap={2} px={3} pb={2}>
                <ArgonInput
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: "300px" }}
                />

                <Dropdown
                  label="Type"
                  value={filterType}
                  options={[
                    { label: "All", value: "" },
                    { label: "Parent", value: "PARENT" },
                    { label: "Tutor", value: "TUTOR" },
                  ]}
                  onChange={(e) => setFilterType(e.target.value)}
                  width={150}
                  size="small"
                />
              </ArgonBox>

              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Delete FAQ"
        content={`Are you sure you want to delete this FAQ: "${selectedFaq?.question}"?`}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default FAQ;
